import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { 
  Clock, 
  Edit3, 
  Play, 
  Save, 
  GitCommit,
  Calendar,
  Timer,
  Activity
} from 'lucide-react';

interface ActivityLog {
  id: string;
  type: 'edit' | 'test' | 'save' | 'configure';
  description: string;
  timestamp: Date;
  duration: number; // en minutos
}

interface WorkflowTracking {
  workflowId: string;
  totalTimeSpent: number; // en minutos
  sessionsCount: number;
  testsRun: number;
  changesCount: number;
  startDate: Date;
  lastActivity: Date;
  activityLog: ActivityLog[];
}

interface WorkflowActivityTrackerProps {
  workflowId: string;
  workflowName: string;
}

export function WorkflowActivityTracker({ workflowId, workflowName }: WorkflowActivityTrackerProps) {
  const [tracking, setTracking] = useState<WorkflowTracking>({
    workflowId,
    totalTimeSpent: 0,
    sessionsCount: 0,
    testsRun: 0,
    changesCount: 0,
    startDate: new Date(),
    lastActivity: new Date(),
    activityLog: [],
  });

  const [sessionStartTime, setSessionStartTime] = useState<Date>(new Date());
  const [currentSessionTime, setCurrentSessionTime] = useState(0);

  // Timer que actualiza cada minuto
  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      const minutesInSession = Math.floor((now.getTime() - sessionStartTime.getTime()) / 60000);
      setCurrentSessionTime(minutesInSession);
    }, 60000); // Cada minuto

    return () => clearInterval(interval);
  }, [sessionStartTime]);

  const logActivity = (type: ActivityLog['type'], description: string, duration: number = 1) => {
    const newActivity: ActivityLog = {
      id: `activity_${Date.now()}`,
      type,
      description,
      timestamp: new Date(),
      duration,
    };

    setTracking(prev => ({
      ...prev,
      totalTimeSpent: prev.totalTimeSpent + duration,
      changesCount: type === 'edit' || type === 'configure' ? prev.changesCount + 1 : prev.changesCount,
      testsRun: type === 'test' ? prev.testsRun + 1 : prev.testsRun,
      lastActivity: new Date(),
      activityLog: [newActivity, ...prev.activityLog].slice(0, 50), // Últimos 50
    }));
  };

  const formatTime = (minutes: number): string => {
    if (minutes < 60) return `${minutes}min`;
    if (minutes < 1440) return `${Math.floor(minutes / 60)}h ${minutes % 60}min`;
    const days = Math.floor(minutes / 1440);
    const hours = Math.floor((minutes % 1440) / 60);
    return `${days}d ${hours}h`;
  };

  const getDaysWorked = (): number => {
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - tracking.startDate.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getActivityIcon = (type: ActivityLog['type']) => {
    switch (type) {
      case 'edit': return <Edit3 className="h-3 w-3" />;
      case 'test': return <Play className="h-3 w-3" />;
      case 'save': return <Save className="h-3 w-3" />;
      case 'configure': return <GitCommit className="h-3 w-3" />;
    }
  };

  const getActivityColor = (type: ActivityLog['type']) => {
    switch (type) {
      case 'edit': return 'bg-blue-500/10 text-blue-600';
      case 'test': return 'bg-green-500/10 text-green-600';
      case 'save': return 'bg-purple-500/10 text-purple-600';
      case 'configure': return 'bg-orange-500/10 text-orange-600';
    }
  };

  // Exponer funciones para que el WorkflowBuilder las use
  useEffect(() => {
    // Esto se llamaría desde WorkflowBuilder cuando ocurran eventos
    (window as any).logWorkflowActivity = logActivity;
  }, []);

  return (
    <Card className="relative overflow-hidden border-border">
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500"></div>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Activity className="h-4 w-4" />
          Tracking de Desarrollo
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Resumen de métricas */}
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 bg-blue-500/10 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <Timer className="h-4 w-4 text-blue-600" />
              <span className="text-xs font-medium text-blue-600">Tiempo Total</span>
            </div>
            <p className="text-xl font-bold">{formatTime(tracking.totalTimeSpent + currentSessionTime)}</p>
            <p className="text-xs text-muted-foreground mt-1">
              En {getDaysWorked()} días
            </p>
          </div>

          <div className="p-3 bg-green-500/10 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <Play className="h-4 w-4 text-green-600" />
              <span className="text-xs font-medium text-green-600">Pruebas</span>
            </div>
            <p className="text-xl font-bold">{tracking.testsRun}</p>
            <p className="text-xs text-muted-foreground mt-1">
              Ejecutadas
            </p>
          </div>

          <div className="p-3 bg-purple-500/10 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <GitCommit className="h-4 w-4 text-purple-600" />
              <span className="text-xs font-medium text-purple-600">Cambios</span>
            </div>
            <p className="text-xl font-bold">{tracking.changesCount}</p>
            <p className="text-xs text-muted-foreground mt-1">
              Iteraciones
            </p>
          </div>

          <div className="p-3 bg-orange-500/10 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <Calendar className="h-4 w-4 text-orange-600" />
              <span className="text-xs font-medium text-orange-600">Sesión Actual</span>
            </div>
            <p className="text-xl font-bold">{formatTime(currentSessionTime)}</p>
            <p className="text-xs text-muted-foreground mt-1">
              En progreso
            </p>
          </div>
        </div>

        {/* Log de actividad */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium">Historial de Actividad</h4>
            <Badge variant="secondary" className="text-xs">
              {tracking.activityLog.length} eventos
            </Badge>
          </div>
          <ScrollArea className="h-64 border border-border rounded-lg">
            <div className="p-2 space-y-1">
              {tracking.activityLog.length === 0 ? (
                <div className="text-center py-8 text-sm text-muted-foreground">
                  Las actividades se registrarán automáticamente
                </div>
              ) : (
                tracking.activityLog.map((activity) => (
                  <div
                    key={activity.id}
                    className={`p-2 rounded ${getActivityColor(activity.type)} flex items-start gap-2`}
                  >
                    <div className="flex h-6 w-6 items-center justify-center rounded flex-shrink-0 mt-0.5">
                      {getActivityIcon(activity.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium">{activity.description}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-xs opacity-70">
                          {activity.timestamp.toLocaleTimeString('es-MX')}
                        </p>
                        <Badge variant="secondary" className="text-xs h-5">
                          {formatTime(activity.duration)}
                        </Badge>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Timeline visual */}
        <div>
          <h4 className="text-sm font-medium mb-2">Timeline</h4>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs">
              <div className="w-20 text-muted-foreground">Inicio:</div>
              <div className="font-medium">{tracking.startDate.toLocaleDateString('es-MX')}</div>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <div className="w-20 text-muted-foreground">Última act:</div>
              <div className="font-medium">{tracking.lastActivity.toLocaleString('es-MX')}</div>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <div className="w-20 text-muted-foreground">Días:</div>
              <div className="font-medium">{getDaysWorked()} días trabajados</div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
